/*
 * Filtra la respuesta de GET_DOMAINS por el area del usuario.
 *
 * Requerimiento: un usuario de cierta area solo debe ver las actividades de su
 * area. El etiquetado vive en `domain.extras` a nivel de ACTIVIDAD ESPECIFICA
 * (la hoja), con la convencion:
 *
 *   - extras.areas = ["Comercial", "Operaciones"]  -> visible solo para esas areas
 *   - (sin extras.areas / sin extras.area)          -> GLOBAL, visible para todos
 *
 * La visibilidad se propaga hacia arriba: un objetivo se muestra si le queda al
 * menos una especifica visible; una general se muestra si le queda al menos un
 * objetivo visible. Asi no hace falta etiquetar los niveles superiores.
 *
 * Si el usuario no tiene area resuelta, NO se filtra nada (se ve todo): evita
 * dejar sin actividades a usuarios cuyo `extras.area` no este configurado.
 */
import { normalizeKey } from '../../shared/verteoffice/exports/helpers/helpers'
import type { DomainConfigItem } from './domainConfig'

/*
 * Interruptor del filtrado de actividades por area.
 *
 *   true  (defecto) -> se valida/escopa: cada usuario ve solo las actividades de
 *                      su area (mas las globales, sin etiqueta).
 *   false           -> se desactiva por completo: todos ven todas las actividades,
 *                      sin importar `extras.areas` ni el area del usuario.
 *
 * Es una constante de codigo (no env) a proposito, para poder prender/apagar el
 * comportamiento sin variables de entorno. Afecta la hoja y ambos modales, que
 * pasan por filterDomainsDataByArea.
 */
export const ACTIVITY_AREA_FILTER_ENABLED = true

interface RawDomainRow {
  id?: string
  name?: string
  parent_id?: string | null
  extras?: any
}

// La respuesta de GET_DOMAINS llega como `any`; el shape exacto de cada fila lo
// fija el llamador. Usamos `any[]` en la firma publica para no chocar con las
// firmas mas estrictas de buildDomainLists / buildActivityMaxHoursLookup.
type DomainsData = Record<string, any[]>

/* Lee las areas de un dominio desde extras. Acepta `areas` (lista) o `area`
   (string). Devuelve null cuando el dominio es global (sin etiqueta). */
const parseDomainAreas = (extras: any): string[] | null => {
  const raw = extras?.areas ?? extras?.area
  if (raw == null) return null
  const list = Array.isArray(raw) ? raw : [raw]
  const normalized = list
    .map(area => normalizeKey(String(area ?? '')))
    .filter(area => area.length > 0)
  return normalized.length > 0 ? normalized : null
}

/*
 * Calcula el conjunto de ids de dominio visibles para `userArea`.
 * Devuelve null cuando no aplica filtro (usuario sin area): el llamador debe
 * usar entonces el conjunto completo sin tocar nada.
 */
export function computeVisibleDomainIds(
  domainsData: DomainsData | undefined,
  config: DomainConfigItem[],
  userArea: string | null | undefined
): Set<string> | null {
  const normUserArea = userArea ? normalizeKey(userArea) : ''
  if (!domainsData || !normUserArea) return null

  interface Node {
    id: string
    parentId: string | null
    areas: string[] | null
  }

  const nodeById = new Map<string, Node>()
  for (const item of config) {
    for (const row of domainsData[item.alias] ?? []) {
      const id = String(row?.id ?? '').trim()
      if (!id || nodeById.has(id)) continue
      nodeById.set(id, {
        id,
        parentId: String(row?.parent_id ?? '').trim() || null,
        areas: parseDomainAreas(row?.extras)
      })
    }
  }

  const childIdsByParent = new Map<string, string[]>()
  for (const node of nodeById.values()) {
    if (!node.parentId) continue
    const siblings = childIdsByParent.get(node.parentId)
    if (siblings) siblings.push(node.id)
    else childIdsByParent.set(node.parentId, [node.id])
  }

  const ownAreaOk = (areas: string[] | null): boolean =>
    areas === null || areas.includes(normUserArea)

  // Poda de abajo hacia arriba con memoizacion. Un nodo es visible si su propia
  // etiqueta permite el area Y (es hoja O tiene algun hijo visible).
  const visibility = new Map<string, boolean>()
  const isVisible = (id: string): boolean => {
    const cached = visibility.get(id)
    if (cached !== undefined) return cached

    const node = nodeById.get(id)
    if (!node) return false

    // Marca temporal para cortar ciclos (el arbol real no los tiene).
    visibility.set(id, false)

    const children = childIdsByParent.get(id) ?? []
    const isLeaf = children.length === 0
    const hasVisibleChild = children.some(childId => isVisible(childId))
    const result = ownAreaOk(node.areas) && (isLeaf || hasVisibleChild)

    visibility.set(id, result)
    return result
  }

  const visible = new Set<string>()
  for (const id of nodeById.keys()) {
    if (isVisible(id)) visible.add(id)
  }
  return visible
}

/*
 * Devuelve una copia de domainsData conservando solo las filas visibles para el
 * area del usuario. Si no aplica filtro, devuelve el objeto original tal cual.
 */
export function filterDomainsDataByArea(
  domainsData: DomainsData | undefined,
  config: DomainConfigItem[],
  userArea: string | null | undefined
): DomainsData | undefined {
  // Interruptor global: apagado -> se ve todo, sin escopar por area.
  if (!ACTIVITY_AREA_FILTER_ENABLED) return domainsData

  const visible = computeVisibleDomainIds(domainsData, config, userArea)
  if (!domainsData || visible === null) return domainsData

  const filtered: DomainsData = {}
  for (const item of config) {
    const rows = domainsData[item.alias] ?? []
    filtered[item.alias] = rows.filter(row =>
      visible.has(String(row?.id ?? '').trim())
    )
  }
  return filtered
}
